import { useCallback, useEffect, useRef, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import { Loader2, Check, Image as ImageIcon, Upload, Trash2 } from 'lucide-react';
import { Modal } from '../../../components/Modal';
import { Avatar } from '../../../components/Avatar';
import { useDialog } from '../../../components/DialogProvider';
import { useToast } from '../../../components/Toast';
import { atendimentoApi } from '../lib/api';
import type { Contato } from '../lib/tipos';

const FOTO_OUTPUT_PX = 480;

function carregarImagem(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

async function gerarJpegBase64Quadrado(srcDataUrl: string, area: Area): Promise<string> {
  const img = await carregarImagem(srcDataUrl);
  const canvas = document.createElement('canvas');
  canvas.width = FOTO_OUTPUT_PX;
  canvas.height = FOTO_OUTPUT_PX;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas indisponivel');
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, FOTO_OUTPUT_PX, FOTO_OUTPUT_PX);
  const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
  return dataUrl.split(',')[1] ?? '';
}

interface Props {
  aberto: boolean;
  contato: Contato;
  onFechar: (atualizado?: Contato) => void;
}

export function ModalUploadFotoContato({ aberto, contato, onFechar }: Props) {
  const toast = useToast();
  const dialog = useDialog();
  const fileRef = useRef<HTMLInputElement>(null);
  const [srcArquivo, setSrcArquivo] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaCrop, setAreaCrop] = useState<Area | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [removendo, setRemovendo] = useState(false);

  const onCropComplete = useCallback((_: Area, areaPx: Area) => setAreaCrop(areaPx), []);

  const aceitarArquivo = useCallback((f: File | null | undefined) => {
    if (!f) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(f.type)) {
      toast.erro('So JPG/PNG/WebP');
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.erro('Imagem maior que 10MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setSrcArquivo(reader.result as string);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setAreaCrop(null);
    };
    reader.readAsDataURL(f);
  }, [toast]);

  function aoEscolherArquivo(e: React.ChangeEvent<HTMLInputElement>) {
    aceitarArquivo(e.target.files?.[0]);
    e.target.value = '';
  }

  // Ctrl+V cola imagem da area de transferencia (so quando modal aberto e ainda sem arquivo escolhido).
  useEffect(() => {
    if (!aberto || srcArquivo) return;
    function onPaste(e: ClipboardEvent) {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const it of items) {
        if (it.type.startsWith('image/')) {
          const f = it.getAsFile();
          if (f) {
            e.preventDefault();
            aceitarArquivo(f);
            return;
          }
        }
      }
    }
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [aberto, srcArquivo, aceitarArquivo]);

  function aoArrastar(e: React.DragEvent) {
    e.preventDefault();
  }

  function aoSoltar(e: React.DragEvent) {
    e.preventDefault();
    aceitarArquivo(e.dataTransfer?.files?.[0]);
  }

  async function salvar() {
    if (!srcArquivo || !areaCrop || enviando) return;
    setEnviando(true);
    try {
      const base64 = await gerarJpegBase64Quadrado(srcArquivo, areaCrop);
      const atualizado = await atendimentoApi.enviarFotoContato(contato.id, { base64, mime: 'image/jpeg' });
      toast.sucesso('Foto atualizada');
      onFechar(atualizado);
    } catch (e: any) {
      toast.erro(e?.message ?? 'Falha ao enviar foto');
    } finally {
      setEnviando(false);
    }
  }

  async function remover() {
    if (removendo) return;
    const ok = await dialog.confirmar('Remover a foto custom? O sistema vai tentar buscar a foto do WhatsApp dele de novo no proximo ciclo.', { perigoso: true });
    if (!ok) return;
    setRemovendo(true);
    try {
      const atualizado = await atendimentoApi.removerFotoContato(contato.id);
      toast.sucesso('Foto custom removida');
      onFechar(atualizado);
    } catch (e: any) {
      toast.erro(e?.message ?? 'Falha ao remover');
    } finally {
      setRemovendo(false);
    }
  }

  const desabilitarFechar = enviando || removendo;

  return (
    <Modal
      aberto={aberto}
      onFechar={desabilitarFechar ? () => {} : () => onFechar()}
      titulo="Trocar foto do contato"
      largura={480}
      rodape={
        <>
          <button type="button" className="btn-secundario" onClick={() => onFechar()} disabled={desabilitarFechar}>
            Cancelar
          </button>
          {srcArquivo && (
            <button
              type="button"
              className="btn-primario flex items-center gap-1.5"
              onClick={salvar}
              disabled={!areaCrop || enviando}
            >
              {enviando ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {enviando ? 'Enviando...' : 'Salvar foto'}
            </button>
          )}
        </>
      }
    >
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={aoEscolherArquivo}
        className="hidden"
      />

      {!srcArquivo && (
        <div className="space-y-3">
          <div className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: 'rgba(0,0,0,0.04)' }}>
            <Avatar nome={contato.nome || contato.push_name || ''} fotoUrl={contato.foto_url} tamanho={56} />
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{contato.nome || contato.push_name || contato.telefone}</div>
              <div className="text-xs opacity-60">
                {contato.foto_custom ? 'Foto custom (subida manualmente)' : contato.foto_url ? 'Foto vinda do WhatsApp' : 'Sem foto'}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            onDragOver={aoArrastar}
            onDrop={aoSoltar}
            className="w-full py-4 border-2 border-dashed rounded-lg hover:bg-black/5 flex flex-col items-center justify-center gap-1 text-sm font-medium"
            style={{ borderColor: 'var(--input-border, rgba(0,0,0,0.2))' }}
          >
            <Upload size={16} /> Escolher imagem (JPG/PNG/WebP)
            <span className="text-[11px] font-normal opacity-60">ou cole com Ctrl+V · ou arraste pra ca</span>
          </button>

          {contato.foto_custom && (
            <button
              type="button"
              onClick={remover}
              disabled={removendo}
              className="w-full py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 flex items-center justify-center gap-1.5"
            >
              {removendo ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
              {removendo ? 'Removendo...' : 'Remover foto custom'}
            </button>
          )}

          <p className="text-[11px] opacity-60 leading-relaxed">
            Util quando o WhatsApp do contato bloqueia a foto pra quem nao esta nos contatos dele.
            Foto custom nunca e sobrescrita pelo sync automatico.
          </p>
        </div>
      )}

      {srcArquivo && (
        <div className="space-y-2">
          <div className="flex items-center gap-1 text-xs opacity-70 font-medium">
            <ImageIcon size={11} /> Ajuste o enquadramento (quadrado)
          </div>
          <div className="relative w-full bg-black/40 rounded" style={{ height: 320 }}>
            <Cropper
              image={srcArquivo}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] opacity-60 w-10">Zoom</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1"
            />
            <button
              type="button"
              onClick={() => { setSrcArquivo(null); setAreaCrop(null); }}
              className="text-xs text-gray-600 hover:text-gray-900 underline"
            >
              Escolher outra
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
